import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";
import { useLocalStorage } from "usehooks-ts";
import { toast } from "@app/ui";
import { shouldShowTimezoneNudge } from "shared/features/timezone/nudge";
import { detectTimezone } from "@/lib/Timezone";
import { useUpdateTimezoneMutation } from "@/queries/User";

// 로그인 후, 브라우저가 감지한 현재 타임존(detectTimezone — OS/브라우저가 아는 현재
// 위치의 IANA 타임존)이 유저에게 저장된 timezone 과 다르면 "바꾸시겠어요?" 소프트
// 토스트를 띄운다. 여행/이동으로 실제 위치가 바뀌었을 때 스케줄 계산 기준(user.timezone)
// 을 최신으로 맞추도록 제안하는 용도다.
//
// 재노출 정책: 유저가 "바꾸기"를 안 누르고 무시한 감지 타임존은 localStorage 에 기억해
// 같은 타임존에서는 다시 띄우지 않는다(성가시지 않게). 이후 또 다른 타임존으로 이동하면
// (감지값이 달라지므로) 그때 다시 제안한다. "바꾸기"를 누르면 user.timezone 이 감지값과
// 같아져 자연히 재노출되지 않는다.

const DISMISSED_TZ_KEY = "timezone-nudge-dismissed";

export function useTimezoneNudge(active: boolean, userTimezone: string | null | undefined): void {
  const { t } = useLingui();
  const updateTimezoneMutation = useUpdateTimezoneMutation();
  const [dismissedTz, setDismissedTz] = useLocalStorage<string | null>(DISMISSED_TZ_KEY, null);

  useEffect(() => {
    const detected = detectTimezone();
    // 띄울지 말지의 결정 규칙은 shared 의 순수 함수가 정본(테스트 대상). 여기서는 감지·
    // 저장·토스트 같은 부수효과만 담당한다.
    const shouldNudge = shouldShowTimezoneNudge({
      active,
      userTimezone,
      detectedTimezone: detected,
      dismissedTimezone: dismissedTz,
    });
    if (!shouldNudge) return;

    // 노출 시점에 기억해 둔다 — 무시하고 닫아도 같은 타임존에서는 다시 뜨지 않는다.
    setDismissedTz(detected);
    const id = toast({
      title: t`현재 위치의 시간대가 설정과 달라요`,
      description: t`현재 위치는 ${detected} 예요. 설정은 ${userTimezone} 로 되어 있어요.`,
      duration: Infinity,
      action: {
        label: t`${detected} 로 바꾸기`,
        onClick: () => {
          updateTimezoneMutation.mutate(detected);
          toast.dismiss(id);
        },
      },
    });
  }, [active, userTimezone, dismissedTz, setDismissedTz, updateTimezoneMutation, t]);
}
